import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys, unwrap, type components } from '@geefamily/api';
import { api } from '@/app/api';
import { useAuth } from '@/features/auth/use-auth';

export type PayoutAddress = components['schemas']['PayoutAddressResponse'];

/** What the site pays prizes in. */
export type PayoutAsset = 'USDT' | 'TRX';

export type PayoutNetwork = 'Tron' | 'Ethereum' | 'BinanceSmartChain';

/**
 * Which networks each asset can be paid on.
 *
 * TRX has exactly one, and that is not a simplification — it is the only chain
 * TRX exists on, so offering a second would let somebody pick a network their
 * prize could never arrive on.
 */
export const networksFor: Record<PayoutAsset, { value: PayoutNetwork; label: string }[]> = {
  USDT: [
    { value: 'Tron', label: 'TRC-20 (Tron)' },
    { value: 'Ethereum', label: 'ERC-20 (Ethereum)' },
    { value: 'BinanceSmartChain', label: 'BEP-20 (BNB Smart Chain)' },
  ],
  TRX: [{ value: 'Tron', label: 'TRC-20 (Tron)' }],
};

export const payoutKeys = queryKeys.module('identity').entity('payout-addresses');

export function usePayoutAddresses() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: payoutKeys.lists(),
    queryFn: () => unwrap<PayoutAddress[]>(api.GET('/api/v1/identity/me/payout-addresses', {})),
    enabled: isAuthenticated,
  });
}

export function useSavePayoutAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      asset,
      network,
      address,
    }: {
      asset: PayoutAsset;
      network: PayoutNetwork;
      address: string;
    }) =>
      unwrap<PayoutAddress>(
        api.PUT('/api/v1/identity/me/payout-addresses/{asset}', {
          params: { path: { asset } },
          body: { network, address },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: payoutKeys.all }),
  });
}

export function useRemovePayoutAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (asset: PayoutAsset) =>
      unwrap<void>(
        api.DELETE('/api/v1/identity/me/payout-addresses/{asset}', {
          params: { path: { asset } },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: payoutKeys.all }),
  });
}
